import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { StarGoalApi } from '@/api/apis/StarGoalApi';
import {
  CreateStarGoalPayload,
  EditStarGoalPayload,
  StarBalance,
  StarGoal,
  StarStreak,
} from '@/api/types';
import { SupervisorContext } from './SupervisorContext';

/**
 * StarContext — barnets stjärnsaldo, streak och mål.
 *
 * Datakälla: StarGoalApi-kontraktet (/star-balance, /star-streak, /star-goals).
 * Stjärn-backenden byggs PARALLELLT (Spår 1). Tills den svarar ger API:ets catch
 * `undefined` → vi normaliserar till NOLLVÄRDEN så att barnvyn aldrig kraschar
 * vid tom/404. INGEN mock används.
 *
 * Projektet använder inte React Query; mönstret följer SupervisorContext (manuell
 * fetch + state + memoiserat value). `refresh()` läser om saldo/streak/mål.
 * Skriv-operationer (skapa/redigera/ta bort/uppfyll) är förälder-handlingar och
 * skickar supervisedTag — backend kräver förälderroll.
 *
 * `selectGoal` är undantaget: att välja vilket mål stjärnorna räknas mot är BARNETS
 * beslut och kräver ingen förälderroll. Den uppdaterar `goals` optimistiskt så att
 * barnet ser sitt val direkt — refresh() efteråt hämtar backendens sanning.
 */

const EMPTY_BALANCE: StarBalance = {
  childId: '',
  availableStars: 0,
  totalEarnedStars: 0,
  totalSpentStars: 0,
};

const EMPTY_STREAK: StarStreak = {
  childId: '',
  currentStreak: 0,
  longestStreak: 0,
  lastCompletionDay: null,
};

export type StarContextType = {
  balance: StarBalance;
  streak: StarStreak;
  goals: StarGoal[];
  /** True under första laddningen (innan något svar/fel hunnit normaliseras). */
  loading: boolean;
  refresh: () => void;
  createGoal: (payload: CreateStarGoalPayload) => Promise<boolean>;
  editGoal: (goalId: string, payload: EditStarGoalPayload) => Promise<boolean>;
  removeGoal: (goalId: string) => Promise<boolean>;
  fulfillGoal: (goalId: string) => Promise<boolean>;
  /** Barnets eget val av vilket aktivt mål nästa stjärna ska räknas mot. */
  selectGoal: (goalId: string) => Promise<boolean>;
};

export const StarContext = createContext<StarContextType>({
  balance: EMPTY_BALANCE,
  streak: EMPTY_STREAK,
  goals: [],
  loading: false,
  refresh: () => {},
  createGoal: async () => false,
  editGoal: async () => false,
  removeGoal: async () => false,
  fulfillGoal: async () => false,
  selectGoal: async () => false,
});

type props = {
  children?: ReactNode;
};

export const StarScene = ({ children }: props) => {
  const { currentClient } = useContext(SupervisorContext);
  const supervisedTag = currentClient ? currentClient.tag : undefined;

  const [balance, setBalance] = useState<StarBalance>(EMPTY_BALANCE);
  const [streak, setStreak] = useState<StarStreak>(EMPTY_STREAK);
  const [goals, setGoals] = useState<StarGoal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    const api = new StarGoalApi();
    setLoading(true);
    try {
      // Kör parallellt — varje endpoint sväljer fel internt (kontraktets catch).
      const [balanceRes, streakRes, goalsRes] = await Promise.all([
        api.getStarBalance({ supervisedTag }),
        api.getStarStreak({ supervisedTag }),
        api.listStarGoals({ supervisedTag }),
      ]);
      // Normalisera saknat svar/404 till nollvärden → vyn kraschar aldrig.
      setBalance(balanceRes ?? EMPTY_BALANCE);
      setStreak(streakRes ?? EMPTY_STREAK);
      setGoals(goalsRes ?? []);
    } finally {
      setLoading(false);
    }
  }, [supervisedTag]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createGoal = useCallback(
    async (payload: CreateStarGoalPayload): Promise<boolean> => {
      const res = await new StarGoalApi().createStarGoal({ payload, supervisedTag });
      const ok = typeof res === 'number' && res >= 200 && res < 300;
      if (ok) await refresh();
      return ok;
    },
    [supervisedTag, refresh],
  );

  const editGoal = useCallback(
    async (goalId: string, payload: EditStarGoalPayload): Promise<boolean> => {
      const res = await new StarGoalApi().editStarGoal({ goalId, payload, supervisedTag });
      const ok = typeof res === 'number' && res >= 200 && res < 300;
      if (ok) await refresh();
      return ok;
    },
    [supervisedTag, refresh],
  );

  const removeGoal = useCallback(
    async (goalId: string): Promise<boolean> => {
      const res = await new StarGoalApi().removeStarGoal({ goalId, supervisedTag });
      const ok = typeof res === 'number' && res >= 200 && res < 300;
      if (ok) await refresh();
      return ok;
    },
    [supervisedTag, refresh],
  );

  const fulfillGoal = useCallback(
    async (goalId: string): Promise<boolean> => {
      const res = await new StarGoalApi().fulfillStarGoal({ goalId, supervisedTag });
      const ok = typeof res === 'number' && res >= 200 && res < 300;
      if (ok) await refresh();
      return ok;
    },
    [supervisedTag, refresh],
  );

  const selectGoal = useCallback(
    async (goalId: string): Promise<boolean> => {
      // Optimistiskt: barnet ska se att valet tog, inte vänta på en nätverksrunda.
      // Exakt ETT mål är valt, så alla andra nollas i samma svep.
      setGoals((previous) => previous.map((goal) => ({ ...goal, selected: goal.id === goalId })));
      const res = await new StarGoalApi().selectStarGoal({ goalId, supervisedTag });
      const ok = typeof res === 'number' && res >= 200 && res < 300;
      // Både vid lyckat och nekat val: läs om från backend. Vid nekat val (målet
      // hunnit bli klart/borttaget) rullas det optimistiska valet tillbaka av sig självt.
      await refresh();
      return ok;
    },
    [supervisedTag, refresh],
  );

  const value = useMemo<StarContextType>(
    () => ({
      balance,
      streak,
      goals,
      loading,
      refresh,
      createGoal,
      editGoal,
      removeGoal,
      fulfillGoal,
      selectGoal,
    }),
    [
      balance,
      streak,
      goals,
      loading,
      refresh,
      createGoal,
      editGoal,
      removeGoal,
      fulfillGoal,
      selectGoal,
    ],
  );

  return <StarContext.Provider value={value}>{children}</StarContext.Provider>;
};

export const useStars = () => useContext(StarContext);
